import React, { useCallback, useMemo, forwardRef, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Icon } from '@zambezi/sdk/icons'
import { classNames, useGlobalId } from '@zambezi/sdk/common'
import { Checkbox } from '@zambezi/sdk/form-elements'
import SelectableCell from './SelectableCell'
import TableRow from './TableRow'

/**
 * @readonly
 * @type {Record<import('./interfaces').Direction, string>}
 */
const ARIA_SORT_DIRECTIONS = {
  ASC: 'ascending',
  DESC: 'descending'
}

/**
 * @readonly
 * @type {Record<import('./interfaces').Direction, import('./interfaces').Direction>}
 */
const NEXT_DIRECTION = {
  DESC: undefined,
  ASC: 'DESC'
}

/**
 * @readonly
 * @type {Record<import('./interfaces').Direction, string>}
 */
const ICON_NAMES = {
  ASC: 'sort-up-xsmall',
  DESC: 'sort-down-xsmall'
}

/**
 * Create a React Table component
 *
 * @template T,P
 * @param {import('./interfaces').TableProps<T, P>} props
 * @param {import('react').Ref} ref
 * @returns {JSX.Element}
 */
function Table (
  {
    columns,
    rows,
    onSort,
    preHeader,
    postHeader,
    withRowSelection,
    withShiftSelection,
    onSelectionChanged,
    selectedIndices,
    isNested,
    isFlushed,
    isResponsive,
    className,
    children,
    rowsOffset,
    rowProps,
    ...tableProps
  },
  ref
) {
  const tableId = useGlobalId('zb-table-')
  const tableClassName = classNames('zb-table', className, {
    'zb-responsive-table': isResponsive,
    'zb-nested-table': isNested,
    'zb-flushed-table': isFlushed,
    'zb-checked-table': withRowSelection
  })

  const allChecked = useMemo(
    () =>
      withRowSelection &&
      withShiftSelection &&
      rows.every(
        (_, index) => selectedIndices.indexOf(index + rowsOffset) !== -1
      ),
    [withRowSelection, withShiftSelection, rowsOffset, rows, selectedIndices]
  )

  const handleSort = useCallback(
    ({
      target: {
        dataset: { column, sortDirection }
      }
    }) => {
      onSort(
        column,
        sortDirection in NEXT_DIRECTION ? NEXT_DIRECTION[sortDirection] : 'ASC'
      )
    },
    [onSort]
  )

  const handleRowSelection = useCallback(
    (rowIndex, _, isChecked) =>
      onSelectionChanged(
        isChecked
          ? selectedIndices.concat(rowIndex)
          : selectedIndices.filter(value => value !== rowIndex),
        isChecked,
        rowIndex
      ),
    [onSelectionChanged, selectedIndices]
  )

  const handleShiftSelection = useCallback(
    (_, isChecked) =>
      onSelectionChanged(
        selectedIndices
          .filter(
            value => value < rowsOffset || value >= rowsOffset + rows.length
          )
          .concat(isChecked ? rows.map((_, index) => rowsOffset + index) : []),
        isChecked,
        null
      ),
    [onSelectionChanged, selectedIndices, rows]
  )

  return (
    <>
      <table className={tableClassName} id={tableId} ref={ref} {...tableProps}>
        <thead>
          {preHeader}
          {columns.length ? (
            <tr className='zb-table-columns'>
              {columns.map(renderHeaderCell)}
            </tr>
          ) : null}
          {postHeader}
        </thead>

        {rows.length ? <tbody>{rows.map(renderRow)}</tbody> : null}

        {children}
      </table>
      <span className='zb-slider-description-sr-only' aria-live='polite'>
        {columns.map(renderSrContent)}
      </span>
    </>
  )

  function renderRowCell (
    { key, formatter, cellProps, Cell = 'td', name = key },
    columnIndex
  ) {
    const {
      row: { [key]: value },
      index,
      isSelected
    } = this
    const content = formatter ? formatter(value, index, key) : value
    const heading = (typeof name === 'string' ? name : key) || null
    const rowIndex = rowsOffset + index
    const isSelectable = !columnIndex && isSelected != null

    return isSelectable ? (
      <SelectableCell
        key={key}
        rowIndex={rowIndex}
        heading={heading}
        cellProps={cellProps}
        Cell={Cell}
        id={`${tableId}-${rowIndex}`}
        onRowSelect={handleRowSelection}
        isChecked={isSelected}
      >
        {content}
      </SelectableCell>
    ) : (
      <Cell key={key} data-heading={heading} {...cellProps}>
        {content}
      </Cell>
    )
  }

  function renderRow (row, index) {
    const isSelected = withRowSelection
      ? selectedIndices.indexOf(index) !== -1
      : null
    return (
      <TableRow
        key={index}
        rowIndex={index + rowsOffset + 1}
        rowProps={rowProps}
        isSelected={isSelected}
      >
        {columns.map(renderRowCell, { row, index, isSelected })}
      </TableRow>
    )
  }

  function renderHeaderCell (
    { key, name = key, headerProps, isSortable, sortDirection },
    index
  ) {
    const id = `${tableId}-all`
    const isSelectable = withRowSelection && withShiftSelection && !index
    return (
      <th
        key={key}
        aria-sort={isSortable ? ARIA_SORT_DIRECTIONS[sortDirection] : null}
        {...headerProps}
      >
        {isSelectable && (
          <Checkbox
            aria-label='Select all rows'
            checked={allChecked}
            id={id}
            onChange={handleShiftSelection}
          />
        )}
        {isSortable ? (
          <button
            onClick={handleSort}
            data-column={key}
            data-sort-direction={sortDirection}
            className='zb-button-link zb-table-button'
            type='button'
            title='Toggle sort'
          >
            {name}
            {ICON_NAMES[sortDirection] && (
              <Fragment>
                {' '}
                <Icon
                  data-column={key}
                  data-sort-direction={sortDirection}
                  name={ICON_NAMES[sortDirection]}
                  size='xsmall'
                  title={ARIA_SORT_DIRECTIONS[sortDirection]}
                />
              </Fragment>
            )}
          </button>
        ) : (
          name
        )}
      </th>
    )
  }

  function renderSrContent ({
    key,
    name = key,
    isSortable,
    sortDirection
  }) {
    if (!isSortable || !ARIA_SORT_DIRECTIONS[sortDirection]) return
    return `${name} sorted ${ARIA_SORT_DIRECTIONS[sortDirection]}`
  }
}

function noop () {}

const table = forwardRef(Table)

table.defaultProps = {
  columns: [],
  rows: [],
  selectedIndices: [],
  rowsOffset: 0,
  rowProps: {},
  onSort: noop,
  onSelectionChanged: noop
}

table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.node,
      formatter: PropTypes.func,
      headerProps: PropTypes.object,
      cellProps: PropTypes.object,
      isSortable: PropTypes.bool,
      sortDirection: PropTypes.oneOf(['ASC', 'DESC']),
      Cell: PropTypes.elementType
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowProps: PropTypes.object,
  rowsOffset: PropTypes.number,
  selectedIndices: PropTypes.arrayOf(PropTypes.number).isRequired,
  onSelectionChanged: PropTypes.func,
  onSort: PropTypes.func,
  preHeader: PropTypes.node,
  postHeader: PropTypes.node,
  isResponsive: PropTypes.bool,
  isNested: PropTypes.bool,
  isFlushed: PropTypes.bool,
  withRowSelection: PropTypes.bool,
  withShiftSelection: PropTypes.bool
}

table.displayName = 'Table'

export default table
